/**
 * Wave 351 · Brain eval schemas — test cases + run results for capability grading.
 */

import { z } from 'zod';
import { isoDateTimeSchema } from './common.js';

/**
 * Wave 351 fix · brain DTO ids cover both kebab-case capability ids
 * and UUIDs. See note in brain-trace.ts.
 */
const idSchema = z.string().min(1).max(128);

// ═══════════════════════════════════════════════════════════════════════════════
// Eval Case
// ═══════════════════════════════════════════════════════════════════════════════

export const EvalCaseDTOSchema = z.object({
  id: idSchema,
  category: z.string().describe('Test category (e.g., "rdo_detection", "fvs_scoring")'),
  capabilityId: idSchema.nullable(),
  name: z.string(),
  description: z.string(),
  graders: z.array(z.string()).describe('Grader names that apply to this case'),
  lastVerdict: z
    .object({
      pass: z.boolean(),
      runId: idSchema,
      finishedAt: isoDateTimeSchema,
      failureReason: z.string().nullable(),
    })
    .nullable(),
});
export type EvalCaseDTO = z.infer<typeof EvalCaseDTOSchema>;

export const EvalCasesListDTOSchema = z.object({
  cases: z.array(EvalCaseDTOSchema),
  _degraded: z.boolean().optional(),
  _reason: z.string().optional(),
});
export type EvalCasesListDTO = z.infer<typeof EvalCasesListDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Eval Run Summary
// ═══════════════════════════════════════════════════════════════════════════════

export const EvalRunSummaryDTOSchema = z.object({
  runId: idSchema,
  triggeredBy: z.string().describe('Trigger (e.g., "manual", "hourly", "pre_deploy")'),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
  passCount: z.number().int().min(0),
  failCount: z.number().int().min(0),
  skipCount: z.number().int().min(0),
});
export type EvalRunSummaryDTO = z.infer<typeof EvalRunSummaryDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Eval Run Result (per case)
// ═══════════════════════════════════════════════════════════════════════════════

export const EvalRunResultDTOSchema = z.object({
  caseId: idSchema,
  pass: z.boolean(),
  graderResults: z.array(z.unknown()),
  failureReason: z.string().nullable(),
});
export type EvalRunResultDTO = z.infer<typeof EvalRunResultDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Eval Runs List
// ═══════════════════════════════════════════════════════════════════════════════

export const EvalRunsListDTOSchema = z.object({
  runs: z.array(EvalRunSummaryDTOSchema),
});
export type EvalRunsListDTO = z.infer<typeof EvalRunsListDTOSchema>;
